/* eslint-disable no-console,@typescript-eslint/no-explicit-any */
import {
  ApolloClient as NativeApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import { NormalizedCacheObject, TypePolicies } from '@apollo/client/cache'
import { onError } from '@apollo/client/link/error'
import fragmentTypes from '@ts/fragments'
import { merge } from 'lodash'
import { GetStaticPropsResult } from 'next'
import { AppProps as NextAppProps } from 'next/app'
import { useMemo } from 'react'

import { APOLLO_STATE_PROP_NAME, ServerProps } from './server'
import { isBrowser, isServer } from './ssrMode'

export const GRAPHQL_API_URL = process.env.GRAPHQL_API_URL || 'https://api.cilabs.com/catalyst'
export const GIT_COMMIT_SHA =
  process.env.VERCEL_GIT_COMMIT_SHA ||
  process.env.VERCEL_GITHUB_COMMIT_SHA ||
  process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
  process.env.NEXT_PUBLIC_VERCEL_GITHUB_COMMIT_SHA ||
  'buildTime'
export const CMS_GRAPHQL_API_URL =
  process.env.CMS_GRAPHQL_API_URL || 'https://websummit.com/graphql'
export const DEFAULT_CONFERENCE_SLUG = process.env.DEFAULT_CONFERENCE_SLUG || 'cc20'
export const DEFAULT_WEB_HOST = process.env.WEB_HOST || 'collisionconf.com'
export type AppProps<T = any> = Omit<NextAppProps<T>, 'pageProps'> & {
  pageProps: ServerProps
}

const typePolicies: TypePolicies = {
  Query: {
    fields: {
      scheduleTimeslot(object, { args, toReference }) {
        if (object) {
          return toReference(object)
        }

        if (typeof args?.slug === 'string') {
          return toReference({ __typename: 'ScheduleTimeslot', slug: args.slug })
        }

        return undefined
      },
      webPageConfigByPathHost(object, { args, toReference }) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const configId = object?.id as string | undefined
        if (configId) {
          return toReference({ __typename: 'WebPageConfig', id: configId })
        }
        const id = [args?.host || '', args?.path || ''].filter(Boolean).join('::')
        return toReference({ __typename: 'WebPageConfig', id })
      },
    },
  },
  ScheduleTimeslot: {
    keyFields: ['slug'],
  },
}

type Headers = Record<string, unknown>

const constructContextHeaders = (commonHeaders: Headers, eventSlug: string, token?: string) => {
  const headers: Headers = {
    ...commonHeaders,
    'x-event-id': eventSlug,
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`
  }
  return headers
}

const createApolloClient = () => {
  const setHeadersLink = new ApolloLink((operation, forward) => {
    const { headers, slug, token } = operation.getContext()
    const eventSlug: string = (slug || DEFAULT_CONFERENCE_SLUG) as string
    const contextHeaders = constructContextHeaders(headers, eventSlug, token)
    operation.setContext({ headers: contextHeaders })
    return forward(operation)
  })

  const httpLink = ApolloLink.split(
    operation => {
      const { wordpress } = operation.getContext()
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return wordpress
    },
    new HttpLink({ uri: CMS_GRAPHQL_API_URL }),
    new HttpLink({
      credentials: 'include',
      uri: ({ operationName }) => {
        const shortPath = `${GRAPHQL_API_URL}`
        const fullPath = `${GRAPHQL_API_URL}/${operationName}`
        return operationName ? fullPath : shortPath
      },
    })
  )

  const errorsLink = onError(({ graphQLErrors, networkError, operation }) => {
    console.log(
      `[GraphQL error] Operation: ${operation.operationName}, Variables: ${JSON.stringify(
        operation.variables
      )}`
    )
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
            locations
          )}, Path: ${JSON.stringify(path)}`
        )
      )
    if (networkError) console.log(`[Network error]: ${JSON.stringify(networkError)}`)
  })

  const link = ApolloLink.from([setHeadersLink, errorsLink, httpLink])
  const { possibleTypes } = fragmentTypes

  return new NativeApolloClient({
    cache: new InMemoryCache({
      possibleTypes,
      typePolicies,
    }),
    connectToDevTools: isBrowser,
    link,
    name: 'VercelFrontend',
    ssrMode: isServer,
    version: `${DEFAULT_WEB_HOST.slice(0, 4)}-${isServer ? 'server' : 'browser'}-${GIT_COMMIT_SHA}`,
  })
}

export type ApolloClient = NativeApolloClient<NormalizedCacheObject>
let apolloClient: ApolloClient | null = null

export const initializeApollo = (initialState: NormalizedCacheObject | null = null) => {
  const _apolloClient = apolloClient ?? createApolloClient()

  if (initialState) {
    const existingCache = _apolloClient.extract()
    _apolloClient.cache.restore(merge(initialState, existingCache))
  }

  if (isServer) return _apolloClient
  if (!apolloClient) apolloClient = _apolloClient

  return _apolloClient
}
export function addApolloState<ServerPropsType extends Record<string, unknown> | undefined>(
  client: ApolloClient,
  pageProps: GetStaticPropsResult<
    ServerPropsType extends Record<string, unknown> ? ServerPropsType : ServerProps
  >
): GetStaticPropsResult<
  ServerPropsType extends Record<string, unknown> ? ServerPropsType : ServerProps
> {
  if (!('props' in pageProps)) return pageProps

  let props = pageProps.props || {}
  if (props) {
    props = { ...props, [APOLLO_STATE_PROP_NAME]: client.cache.extract() }
  }
  return { ...pageProps, props }
}

export const useApollo = (pageProps: AppProps['pageProps']) => {
  const state = pageProps[APOLLO_STATE_PROP_NAME]
  return useMemo(() => initializeApollo(state), [state])
}
